/**
 * Compte cloud (client) — connexion facultative qui sauvegarde TOUTE la progression locale
 * chez le fournisseur (Firebase en prod), pour la retrouver sur n'importe quel appareil.
 * Jouer sans compte reste possible : le compte n'est qu'une sauvegarde en ligne par-dessus.
 *
 * Ce module ne connaît pas le fournisseur : il délègue auth + stockage à un `CloudBackend`
 * (Firebase réel ou simulateur d'e2e). Modèle : dernier écrivain gagne (LWW par `updatedAt`).
 * - Inscription : le compte adopte la progression locale (push).
 * - Connexion : on restaure le compte (la sauvegarde cloud écrase le local).
 * - En jeu : chaque changement déclenche un push différé.
 * - Reprise de session (déjà connecté) : pull si le cloud est plus récent (multi-appareils).
 */
import { getCloudBackend } from './cloud';
import type { AuthResult, CloudBackend, CloudUser } from './cloud/types';

const SYNC_KEY = 'oc_v11_sync'; // updatedAt de la dernière synchro appliquée localement

let backend: CloudBackend | null = null;
let user: CloudUser | null = null;
let syncTimer: number | null = null;
let firstAuth = true;
let onReplaced: (() => void) | null = null;
let onAuthChange: (() => void) | null = null;

export function setOnDataReplaced(cb: () => void): void {
  onReplaced = cb;
}
export function setOnAuthChange(cb: () => void): void {
  onAuthChange = cb;
}

/** Initialise le cloud et l'observateur de session (à appeler une fois au boot). */
export function initCloud(): void {
  backend = getCloudBackend();
  if (!backend) return;
  backend.onAuth((u) => {
    user = u;
    onAuthChange?.();
    // Première notification (reprise de session persistée) : tirer une sauvegarde plus récente.
    if (firstAuth) {
      firstAuth = false;
      if (u) void pullSave(false);
    }
  });
}

export function isCloudAvailable(): boolean {
  return backend !== null;
}
export function isLoggedIn(): boolean {
  return user !== null;
}
export function currentEmail(): string | null {
  return user?.email ?? null;
}

function localSyncAt(): number {
  return Number(localStorage.getItem(SYNC_KEY) ?? '0') || 0;
}
function setSyncAt(t: number): void {
  try {
    localStorage.setItem(SYNC_KEY, String(t));
  } catch {
    /* ignore */
  }
}

/** Rassemble toutes les clés de données de JEU (progression/score/profils), hors réglages & interne. */
export function gatherGameData(): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('oc_v')) continue;
    if (k === 'oc_v9_settings' || k === SYNC_KEY) continue;
    const v = localStorage.getItem(k);
    if (v != null) out[k] = v;
  }
  return out;
}

/** Remplace les données de jeu locales par le blob cloud (efface d'abord les clés existantes). */
function applyGameData(data: Record<string, unknown>): void {
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('oc_v') && k !== 'oc_v9_settings' && k !== SYNC_KEY) toRemove.push(k);
  }
  for (const k of toRemove) localStorage.removeItem(k);
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === 'string') localStorage.setItem(k, v);
  }
}

/** Inscription : crée le compte et y pousse la progression locale. */
export async function register(email: string, password: string): Promise<AuthResult> {
  if (!backend) return { ok: false, error: 'Compte indisponible.' };
  const res = await backend.signUp(email, password);
  if (res.ok) await pushSave(); // le nouveau compte adopte la progression actuelle
  return res;
}

/** Connexion : restaure le compte (la sauvegarde cloud écrase le local). */
export async function login(email: string, password: string): Promise<AuthResult> {
  if (!backend) return { ok: false, error: 'Compte indisponible.' };
  const res = await backend.signIn(email, password);
  if (res.ok) await pullSave(true);
  return res;
}

/** Envoie l'email « mot de passe oublié » (le fournisseur envoie le lien de réinitialisation). */
export async function sendPasswordReset(email: string): Promise<AuthResult> {
  if (!backend) return { ok: false, error: 'Compte indisponible.' };
  return backend.sendPasswordReset(email);
}

/** Déconnexion (les données locales restent sur l'appareil). */
export async function logout(): Promise<void> {
  await backend?.signOut();
}

/** Pousse la progression locale vers le cloud (best-effort). */
export async function pushSave(): Promise<void> {
  if (!backend || !user) return;
  try {
    await backend.saveData(gatherGameData());
    setSyncAt(Date.now());
  } catch {
    /* hors ligne : on retentera au prochain changement */
  }
}

/**
 * Tire la sauvegarde cloud et l'applique si `force` OU si elle est plus récente que la dernière
 * synchro locale. Renvoie true si les données locales ont été remplacées.
 */
export async function pullSave(force = false): Promise<boolean> {
  if (!backend || !user) return false;
  try {
    const payload = await backend.loadData();
    if (payload && (force || payload.updatedAt > localSyncAt())) {
      applyGameData(payload.data);
      setSyncAt(payload.updatedAt);
      onReplaced?.();
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** Programme un push différé (regroupe les changements rapprochés). Appelé après chaque save local. */
export function scheduleSync(): void {
  if (!backend || !user) return;
  if (syncTimer != null) clearTimeout(syncTimer);
  syncTimer = (globalThis.setTimeout as typeof setTimeout)(() => {
    syncTimer = null;
    void pushSave();
  }, 2500) as unknown as number;
}
